/**
 * useBuddyGroups — List all buddy groups the current user belongs to.
 *
 * Wraps buddy-group.service.listMyBuddyGroups with React Query caching.
 * Returns BuddyGroupSummary[] for the list page.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../state_management_and_stores/auth.store';
import { buddyGroupOpsKeys } from '../api/extensions/useBuddyGroupOps';
import { listMyBuddyGroups } from '../services/buddy-group.service';
import type { BuddyGroupSummary } from '../types/buddy-groups';

/** Extended query keys for buddy group list queries. */
export const buddyGroupListKeys = {
  myGroups: (userProfileId: string) =>
    [...buddyGroupOpsKeys.all, 'my-groups', userProfileId] as const,
};

/**
 * Fetch all buddy groups the current user belongs to.
 *
 * Returns an array of BuddyGroupSummary objects with member count,
 * is_primary flag, and group status.
 */
export function useBuddyGroups() {
  const { profile } = useAuthStore();
  const userProfileId = profile?.id ?? '';

  return useQuery<BuddyGroupSummary[]>({
    queryKey: buddyGroupListKeys.myGroups(userProfileId),
    queryFn: () => listMyBuddyGroups(userProfileId),
    enabled: !!userProfileId,
    staleTime: 2 * 60 * 1000, // 2 min — buddy groups change infrequently
  });
}
